import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import { isEmpty, isEqual, refIsRequiredError, toNumber } from "@util/utils";
import { AutocompleteField, Card, DateField, TextField, Typography, Box, Alert, Stack, CheckboxField, RadioField } from "@curves/ui-base";
import { Grid } from "@mui/material";
import { displayDateText, getToday } from "@util/moment";
import usePeopleApi from "@apis/usePeopleApi";
import { PEOPLE_TYPE } from "@icoach/constants/";
import { FITNESSBANDAGREEMENTTEXT } from "@icoach/constants/appConfig";
import { ORDER_INVOICE_ISSUING_TYPE, INVOICE_CARRIER_TYPE } from "@icoach/constants/";
import { ReadTextField, SignBox } from "@icoach/components";
import { PaidList, NoticeCard } from "@icoach/documents/components";
import { PaidListLayout, PaidListTotalGroup } from "@icoach/documents/components/PaidList";
import useDeviceOrderApi from "@apis/useDeviceOrderApi";
import { useSmartFitnessContext } from "@icoach/smartFitnessBand/components/SmartFitnessContext";
import useQueryManager from "@util/hook/useQueryManager";
import useValidationErrors from "@util/hook/useValidationErrors";
import PaymentSection from "@icoach/smartFitnessBand/components/PaymentSection";
import DiscountPlanSelect from "@icoach/smartFitnessBand/components/DiscountPlanSelect";
import { PROMOTIONAL_PLAN_FREE_TYPE } from "@icoach/constants/dataEnums";

// 會員資訊 (租用人)
export const ContractMemberInfo = React.forwardRef((props, ref) => {
	const { contractApplySourceData, setApplySourceData } = useSmartFitnessContext();
	const { memberInfo } = contractApplySourceData;
	const { people: memberOptions = [], isLoaded } = usePeopleApi(PEOPLE_TYPE.MEMBER);

	const { getDeviceOrderMemberInfoApi } = useDeviceOrderApi();
	const { memberNo, memberIdentityNumber, memberAddress, memberPhone, memberEmail, canUseDevice, canUseDeviceText } = memberInfo;
	const memberIDRef = useRef(null);
	const { searchParams } = useQueryManager();

	const onInputChange = target => {
		const { id } = target || {};
		const memberID = toNumber(id);
		if (isEqual(memberID, 0) || !memberID) setApplySourceData();
		else getMemberInfoApi(memberID);
	};

	const getMemberInfoApi = async memberID => {
		const resp = await getDeviceOrderMemberInfoApi(memberID);
		if (resp) {
			setApplySourceData(resp);
		}
	};

	useImperativeHandle(
		ref,
		() => {
			return {
				getResult: () => {
					const { id = 0 } = memberIDRef.current.getResult();
					return {
						memberID: id,
						canUseDevice: canUseDevice,
						canUseDeviceText: canUseDeviceText,
					};
				},
				isError: () => refIsRequiredError(memberIDRef),
				canUseDevice: canUseDevice,
			};
		},
		// eslint-disable-next-line
		[canUseDevice],
	);

	useEffect(
		() => {
			if (!isEmpty(memberOptions) && searchParams.memberID) {
				getMemberInfoApi(searchParams.memberID);
			}
		},
		// eslint-disable-next-line
		[searchParams.memberID, memberOptions],
	);

	return (
		<React.Fragment>
			<Card.CardHeader title="會員資料" className="bg-light text-center" />
			<Card.CardContent className="px-4 py-4">
				<Grid spacing={3} container>
					<Grid xs={6} item>
						<AutocompleteField
							key={`${searchParams?.memberID}${isLoaded}`}
							defaultValue={searchParams?.memberID}
							ref={memberIDRef}
							options={memberOptions}
							label="會員"
							optionKeys={["name", "nickName", "mobile", "no"]}
							optionMaskKeys={["maskName", "nickName", "maskMobile", "no"]}
							primaryKey="id"
							inputProps={{
								placeholder: "請輸入會員姓名/手機號碼/會員編號，擇一即可",
							}}
							onChange={onInputChange}
							fullWidth
							required
						/>
					</Grid>
					<Grid xs={6} item>
						<ReadTextField label="會員編號">{memberNo}</ReadTextField>
					</Grid>
					<Grid xs={6} item>
						<ReadTextField label="身份證字號">{memberIdentityNumber}</ReadTextField>
					</Grid>
					<Grid xs={6} item>
						<ReadTextField label="地址">{memberAddress}</ReadTextField>
					</Grid>
					<Grid xs={6} item>
						<ReadTextField label="行動電話號碼">{memberPhone}</ReadTextField>
					</Grid>
					<Grid xs={6} item>
						<ReadTextField label="電子信箱Email">{memberEmail}</ReadTextField>
					</Grid>
					{!isEmpty(canUseDeviceText) && (
						<Grid xs={12} item>
							<Alert variant="filled" severity="warning" color="warning">
								{canUseDeviceText}
							</Alert>
						</Grid>
					)}
				</Grid>
			</Card.CardContent>
		</React.Fragment>
	);
});

// 合約資訊
export const ContractMainSection = React.forwardRef((props, ref) => {
	const { getRangeFeeApi } = props;
	const { contractApplySourceData, options } = useSmartFitnessContext();
	const { mainSection } = contractApplySourceData;
	const { monthFee, subscriptionStartDate, firstMonthFee, debitFirstDate, trainingFee, planFreeType } = mainSection;
	const { discountPlanOptions } = options;
	const discountPlanRef = useRef(null);
	const [planKeyForRadio, setPlanKeyForRadio] = useState(0);
	const [total, setTotal] = useState(0);
	const checkoutPaymentRef = useRef(null);
	const debitFirstDateRef = useRef(null);
	const preGiftCheckboxRef = useRef(null); // 前置贈送天數 checkbox
	const { checkoutPaymentTypeOptions } = options;
	const hasPreDays = isEqual(planFreeType, PROMOTIONAL_PLAN_FREE_TYPE.PRESUBSCRIPTION); // 是否有前置贈送天數

	useEffect(
		() => {
			setTotal(firstMonthFee + trainingFee);
		},
		// eslint-disable-next-line
		[firstMonthFee, trainingFee],
	);

	useEffect(
		() => {
			setPlanKeyForRadio(0);
		},
		// eslint-disable-next-line
		[contractApplySourceData.memberInfo.memberID],
	);

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				// 有前置贈送天數時，強制設為自動扣款 (0)
				const checkoutPaymentType = hasPreDays ? 0 : checkoutPaymentRef.current.getResult().find(({ checked }) => checked)?.value;
				const { planID, planType } = discountPlanRef.current?.getResult?.() ?? { planID: 0, planType: 0 };

				return {
					checkoutPaymentType: checkoutPaymentType,
					plan: { planID, planType },
				};
			},
			isError: () => {
				return refIsRequiredError(discountPlanRef, preGiftCheckboxRef, checkoutPaymentRef);
			},
		}),
		// eslint-disable-next-line
		[hasPreDays],
	);

	return (
		<React.Fragment>
			<Card.CardHeader title="合約資訊" className="bg-light text-center" />
			<Card.CardContent className="p-4">
				<Grid spacing={3} container>
					<Grid xs={6} item>
						<ReadTextField label="月租定價">{monthFee}</ReadTextField>
					</Grid>
					<Grid xs={6} item>
						<DateField label="申請日期" defaultValue={getToday()} fullWidth required disabled />
					</Grid>
					<Grid xs={6} item>
						<ReadTextField label="訂閱起始日">{displayDateText(subscriptionStartDate)}</ReadTextField>
					</Grid>
					<Grid xs={6} item>
						<DiscountPlanSelect
							key={contractApplySourceData.memberInfo.memberID}
							ref={discountPlanRef}
							options={discountPlanOptions}
							getRangeFeeApi={getRangeFeeApi}
							onChange={setPlanKeyForRadio}
						/>
					</Grid>
				</Grid>
			</Card.CardContent>
			{/* 費用資訊 */}
			<Card.CardHeader title="費用明細" className="bg-light text-center" />
			<Card.CardContent className="p-4">
				{/* 在選擇優惠折扣時，顯示前置贈送天數資訊 */}
				{hasPreDays && subscriptionStartDate && (
					<React.Fragment>
						<Alert className="mb-3" severity="info" variant="filled" color="info">
							本次訂閱包含免費體驗期間，體驗期間內不收費；體驗期結束隔日
							<Typography component="span" className="mark">
								{displayDateText(subscriptionStartDate)}
							</Typography>
							將自動扣款。
						</Alert>
						<CheckboxField className="mb-3" ref={preGiftCheckboxRef} helperText="請確認已閱讀完畢並勾選遵守須知" required>
							<CheckboxField.Checkbox label="我理解本服務含服務體驗期，期滿若未取消，系統將依條款自動轉為正式訂閱，首次扣款依實際天數比例計算，之後每月將自動續扣，金額依方案與條款為準。" />
						</CheckboxField>
					</React.Fragment>
				)}
				{/*  每月扣款資訊  */}
				<Grid spacing={3} container>
					<Grid xs={6} item>
						<PaidListLayout>
							<Box>
								每月
								<Typography className="mark">1</Typography>
								日扣款
							</Box>
							<Grid container spacing={3}>
								<Grid item xs={12}>
									<DateField
										key={uuid()}
										ref={debitFirstDateRef}
										defaultValue={debitFirstDate ? displayDateText(new Date(debitFirstDate)) : null}
										label={"自此月份開始"}
										views={["year", "month"]}
										openTo={"month"}
										format="YYYY/MM"
										mask={"____/__"}
										disabled
										fullWidth
										required
									/>
								</Grid>
							</Grid>
						</PaidListLayout>
					</Grid>
					{/* 費用合計 */}
					<Grid xs={6} item>
						<PaidListLayout>
							<PaidList.PaidListItem label="初次付款">
								{/* 有前置贈送天數時，強制設為自動扣款 (0) */}
								{/* 變更行銷方案Key時，會清空選項 */}
								<RadioField key={planKeyForRadio} ref={checkoutPaymentRef} required={!hasPreDays} row disabled={hasPreDays} defaultValue={hasPreDays}>
									{checkoutPaymentTypeOptions.map(({ text, value }) => (
										<RadioField.Radio key={value} value={value} label={text} />
									))}
								</RadioField>
							</PaidList.PaidListItem>
							<PaidList.PaidListItem label="開通設定費">
								<TextField value={trainingFee} maskType="MONEY" required disabled />
							</PaidList.PaidListItem>
                            <PaidList.PaidListItem label="首月破月費">
								<TextField value={firstMonthFee} maskType="MONEY" required disabled />
							</PaidList.PaidListItem>
							<PaidListTotalGroup total={total} />
						</PaidListLayout>
					</Grid>
				</Grid>
			</Card.CardContent>
		</React.Fragment>
	);
});

// 付款資訊(信用卡付款資訊)
export const ContractPayment = React.forwardRef((props, ref) => {
	const { errorModule: errorModuleProps = [] } = props;
	const { contractApplySourceData } = useSmartFitnessContext();
	const { validationErrors, updateValidationErrors, clearValidationErrors } = useValidationErrors();
	const { memberInfo } = contractApplySourceData;
	const paymentSectionRef = useRef(null);

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => paymentSectionRef.current.getResult(),
			isError: () => refIsRequiredError(paymentSectionRef),
		}),
		// eslint-disable-next-line
		[],
	);

	useEffect(
		() => {
			if (!isEmpty(errorModuleProps)) {
				updateValidationErrors(errorModuleProps);
			} else {
				clearValidationErrors();
			}
		},
		// eslint-disable-next-line
		[JSON.stringify(errorModuleProps)],
	);

	return <PaymentSection titleClassName="text-center" ref={paymentSectionRef} memberID={memberInfo?.memberID} errorModule={validationErrors} />;
});

// 發票資訊
export const ContractInvoice = React.forwardRef((props, ref) => {
	const { contractApplySourceData } = useSmartFitnessContext();
	const { invoice } = contractApplySourceData || {};

	return (
		<React.Fragment>
			<React.Fragment>
				<Card.CardHeader title="發票資訊" className="bg-light text-center" />
				<Card.CardContent className="p-4">
					<Grid spacing={3} container>
						<Grid xs={4} item>
							<ReadTextField label="發票類型">{invoice.issuingTypeText}</ReadTextField>
						</Grid>
						{isEqual(invoice.issuingType, ORDER_INVOICE_ISSUING_TYPE.DONATE) && (
							<Grid xs={4} item>
								<ReadTextField label="愛心碼">{invoice.donationCode}</ReadTextField>
							</Grid>
						)}
						{isEqual(invoice.issuingType, ORDER_INVOICE_ISSUING_TYPE.DUPLICATEUNIFORMINVOICE) && (
							<React.Fragment>
								<Grid xs={4} item>
									<ReadTextField label="發票載具">{invoice.carrierTypeText}</ReadTextField>
								</Grid>
								{[INVOICE_CARRIER_TYPE.MOBILEBARCODE, INVOICE_CARRIER_TYPE.CITIZENDIGITALCERTIFICATE].some(x => isEqual(invoice.carrierType, x)) && (
									<Grid xs={4} item>
										<ReadTextField label="載具號碼">{invoice.carrierNo}</ReadTextField>
									</Grid>
								)}
							</React.Fragment>
						)}
						{isEqual(invoice.issuingType, ORDER_INVOICE_ISSUING_TYPE.TRIPLICATEUNIFORMINVOICE) && (
							<React.Fragment>
								<Grid xs={4} item>
									<ReadTextField label="統一編號">{invoice.taxID}</ReadTextField>
								</Grid>
								<Grid xs={4} item>
									<ReadTextField label="發票抬頭">{invoice.taxTitle}</ReadTextField>
								</Grid>
							</React.Fragment>
						)}
						<Grid xs={4} item>
							<ReadTextField label="寄送載具信箱">{invoice.mail}</ReadTextField>
						</Grid>
						<Grid xs={4} item>
							<ReadTextField label="通知手機號碼">{invoice.cellPhone}</ReadTextField>
						</Grid>
						{!isEmpty(invoice.issuingType) && (
							<Grid xs={12} item>
								<Alert severity="info" variant="filled" color="info">
									若要修改發票資訊請至會員管理頁面進行修改
								</Alert>
							</Grid>
						)}
					</Grid>
				</Card.CardContent>
			</React.Fragment>
		</React.Fragment>
	);
});

// 訂購須知
export const ContractAgreement = React.forwardRef((props, ref) => {
	const version = 101;
	const t = FITNESSBANDAGREEMENTTEXT[version];
	const noticeCardRef = useRef([]);
	const checkBoxRef = useRef(null);
	const signRef = useRef(null);

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				return {
					notice: noticeCardRef.current.map(el => el.getResult().checked),
					agree: checkBoxRef.current.getResult().checked,
					sign: signRef.current.getResult(),
				};
			},
			isError: () => refIsRequiredError(...noticeCardRef.current, checkBoxRef, signRef),
		}),
		// eslint-disable-next-line
		[],
	);

	return (
		<React.Fragment>
			<Card.CardHeader title="智能系統使用須知" className="bg-light text-center" />
			<Card.CardContent className="px-4 py-5">
				{t.map(({ title, items, img }, _index) => (
					<NoticeCard ref={el => (noticeCardRef.current[_index] = el)} key={_index} title={title} noticeData={items} img={img} required />
				))}
				<Alert className="mb-3" severity="info" variant="filled" color="info">
					因涉及會員權益與金額之認定，務必親自赴所屬店舖辦理。
				</Alert>
				<Stack direction="column" justifyContent="center" alignItems="center">
					<CheckboxField ref={checkBoxRef} helperText="請確認已閱讀完畢並勾選遵守須知" required className="mb-3">
						<CheckboxField.Checkbox label="本人已瞭解並詳讀《智能系統訂閱服務使用者條款》，並同意遵守。" />
					</CheckboxField>
					<SignBox ref={signRef} title="會員簽名" required />
				</Stack>
			</Card.CardContent>
		</React.Fragment>
	);
});

export const ContractNote = React.forwardRef((props, ref) => {
	return (
		<React.Fragment>
			<Card.CardHeader title="備註" className="bg-light text-center" />
			<Card.CardContent className="px-4 py-5">
				<TextField ref={ref} multiline rows={4} fullWidth />
			</Card.CardContent>
		</React.Fragment>
	);
});

/* 終止使用*/
// 信用卡付款資訊
export const TermContent = props => {
	const { sourceData, title, subheader, describe = "", children = null } = props;

	return (
		<Card>
			<Card.CardHeader title={title} subheader={subheader} className="bg-light-active text-center" />
			<Card.CardContent className="p-4">
				{describe && (
					<Typography variant="h5" component="div" className="pl-2 mb-2">
						{describe}
					</Typography>
				)}
				{Array.isArray(sourceData) && (
					<Box component="ol" className="lists ol mb-3">
						{sourceData.map((text, i) => (
							<Box component="li" key={i} className="lists-item">
								{text}
							</Box>
						))}
					</Box>
				)}
				{children}
			</Card.CardContent>
		</Card>
	);
};
